import * as _ from 'lodash';
import { bidColumns, defaultColumns } from 'external/features/bids/bidColumns';
import BidsComponentParameters from 'external/features/bids/bidComponentParameters';
import React, { useEffect, useState } from 'react';
import { Accordion, Button, Card, Form } from 'react-bootstrap';

import './BidComponentBuilder.scss';
import ColumnConfiguration from 'common/agGrid/columnConfiguration';
import useStartup from 'common/ui/useStartup';
import Loading from 'common/ui/Loading';
import CopyToClipboard from 'common/ui/CopyToClipboard';
import externalComponentService from './externalComponentService';
import PhysicalLocation from 'domain/physicalLocation';
import Commodity from 'domain/commodity';
import merchantService from '../merchantService';
import StylingHelp from './StylingHelp';
import userPreferencesService from 'admin/common/userPreferencesService';
import ColumnConfigurationModal from 'common/agGrid/ColumnConfigurationModal';

export interface BidComponentBuilderProps {
    merchantId: string;
}

const defaultParameters: BidsComponentParameters = {
    apiKey: null,
    columns: defaultColumns,
    commodities: null,
    locations: null,
    byCommodity: false,
    hideRowsWithoutBasis: true,
    preview: false
}

const columnConfigs = _.map(bidColumns, m => ({
    id: m.key,
    displayName: m.name
}));

const BUILDER_PREFERENCE_KEY = "Admin_ExternalBids";

const BidComponentBuilder: React.FC<BidComponentBuilderProps> = props => {

    const [params, setParams] = useState<BidsComponentParameters>(defaultParameters);
    const [url, setUrl] = useState<string>(null);
    const [editColumns, setEditColumns] = useState(false);
    const [editCommodities, setEditCommodities] = useState(false);
    const [editLocations, setEditLocations] = useState(false);

    const [commodities, setCommodities] = useState<Commodity[]>(null);
    const [locations, setLocations] = useState<PhysicalLocation[]>(null);

    const [commodityConfigs, setCommodityConfigs] = useState<ColumnConfiguration[]>(null);
    const [locationConfigs, setLocationConfigs] = useState<ColumnConfiguration[]>(null);
    const [css, setCss] = useState<string>("");
    const [needsPreview, setNeedsPreview] = useState(false);
    const [needsPublish, setNeedsPublish] = useState(false);
    const [lastUpdated, setLastUpdated] = useState<number>(0);

    useStartup(() => {
        externalComponentService.getBidStyle(props.merchantId)
            .then(m => setCss(m ?? ""));
        merchantService.listCommodities(props.merchantId)
            .then(m => setCommodities(m));
        merchantService.listLocations(props.merchantId)
            .then(m => setLocations(m));
        userPreferencesService.getPreferences(props.merchantId, BUILDER_PREFERENCE_KEY)
            .then(m => {
                const val = m.length === 1 ? JSON.parse(m[0]?.value) : null;

                if (val != null) setParams({...params, ...val});
            })
    });

    useEffect(() => {
        setLocationConfigs(locations?.map(m => ({ id: m.id, displayName: m.name })));
    }, [locations])

    useEffect(() => {
        setCommodityConfigs(commodities?.map(m => ({ id: m.id.toString(), displayName: m.displayName })));
    }, [commodities])

    useEffect(() => {
        var urlParams = new URLSearchParams();
        
        urlParams.set("key", params.apiKey);
        
        if (params.columns !== defaultColumns) urlParams.set("cols", params.columns.join(','));
        else urlParams.delete("cols");
        
        if (params.commodities?.length > 0) urlParams.set("comms", params.commodities.join(','));
        else urlParams.delete("comms");
        
        if (params.locations?.length > 0) urlParams.set("locs", params.locations.join(','));
        else urlParams.delete("locs");

        if (params.byCommodity) urlParams.set("byComm", "true");
        else urlParams.delete("byComm");

        if (params.hideRowsWithoutBasis) urlParams.set("hideRows", "true");
        else urlParams.delete("hideRows");

        setUrl(`https://${window.location.host}/component/bids?${urlParams}`);
    }, [params]);

    async function updateParams(newVal: BidsComponentParameters) {
        setParams(newVal);
        await userPreferencesService.updatePreference(props.merchantId, BUILDER_PREFERENCE_KEY, JSON.stringify(newVal));
    }

    async function onPreview() {
        await externalComponentService.setBidStyle(props.merchantId, css, true);
        setLastUpdated(Date.now());
        setNeedsPublish(true);
        setNeedsPreview(false);
    }

    async function onPublish() {
        externalComponentService.setBidStyle(props.merchantId, css, false);
        setLastUpdated(Date.now());
        setNeedsPreview(false);
        setNeedsPublish(false);
    }

    function getIframeCode(): string {
        return `<iframe src="${url}" \n\twidth="100%" \n\theight="300px" \n\tstyle="border: none" />`;
    }

    if (locationConfigs == null || commodityConfigs == null) return <Loading />

    return <div className="bid-component-builder">
        <div className="row">
            <div className="col">
                <h3>Settings</h3>

                <Form.Group className="form-inline">
                    <Form.Label className="pr-2">API Key</Form.Label>
                    <Form.Control
                        value={params.apiKey ?? ""}
                        onChange={ev => updateParams({...params, apiKey: ev.target.value })} />
                </Form.Group>


                <Button className="mr-2" onClick={() => setEditColumns(true)}>Choose Columns</Button>
                <Button className="mr-2" onClick={() => setEditCommodities(true)}>Choose Commodities</Button>
                <Button className="mr-2" onClick={() => setEditLocations(true)}>Choose Locations</Button>

                <Form.Group className="form-inline">
                    <Form.Label className="pr-2">Group By Commodity First</Form.Label>
                    <Form.Switch id="by-commodity"
                        checked={params.byCommodity}
                        onChange={ev => updateParams({...params, byCommodity: ev.target.checked})} />
                </Form.Group>

                <Form.Group className="form-inline">
                <Form.Label className="pr-2">Hide Rows Without Basis</Form.Label>
                    <Form.Switch id="hide-rows-without-basis"
                        checked={params.hideRowsWithoutBasis}
                        onChange={ev => updateParams({...params, hideRowsWithoutBasis: ev.target.checked})} />
                </Form.Group>

                {editColumns && <ColumnConfigurationModal
                    title="Table Columns"
                    allColumns={columnConfigs}
                    selectedColumns={params.columns.map(m => columnConfigs.find(c => c.id === m))}
                    onSave={cols => {
                        updateParams({...params, columns: cols.map(m => m.id) });
                        setEditColumns(false);
                    }}
                    onClose={() => setEditColumns(false)}
                    />}
                
                
                {editCommodities && <ColumnConfigurationModal 
                    title="Commodities"
                    allColumns={commodityConfigs}
                    selectedColumns={params.commodities?.map(m => commodityConfigs.find(c => c.id === m.toString())) ?? commodityConfigs}
                    onSave={vals => {
                        updateParams({...params, commodities: vals.map(m => Number.parseInt(m.id)) });
                        setEditCommodities(false);
                    }}
                    onClose={() => setEditCommodities(false)}
                    />
                }

                {editLocations && <ColumnConfigurationModal 
                    title="Locations"
                    allColumns={locationConfigs}
                    selectedColumns={params.locations?.map(m => locationConfigs.find(c => c.id === m)) ?? locationConfigs}
                    onSave={vals => {
                        updateParams({...params, locations: vals.map(m => m.id) });
                        setEditLocations(false);
                    }}
                    onClose={() => setEditLocations(false)}
                    />
                }

                <h3>Styling</h3>

                <Form.Control as="textarea" rows={10}
                    value={css}
                    onChange={ev => {
                        setCss(ev.target.value);
                        setNeedsPreview(true);
                        setNeedsPublish(false);
                    }} />
                
                <div className="mt-2">
                    <Button className="mr-2" onClick={() => onPreview()} disabled={!needsPreview}>Preview</Button>
                    <Button onClick={() => onPublish()} variant="danger" disabled={!needsPublish}>Publish</Button>
                </div>

                <Accordion className="mt-2">
                    <Card>
                        <Card.Header>
                            <Accordion.Toggle as={Button} variant="link" eventKey="1">Styling Help</Accordion.Toggle>
                        </Card.Header>
                        <Accordion.Collapse eventKey="1">
                            <Card.Body>
                                <StylingHelp />
                            </Card.Body>
                        </Accordion.Collapse>
                    </Card>
                </Accordion>
            </div>
            <div className="col">
                <span className="h3">Preview</span> 
                <a href={url} target="_blank" rel="noopener noreferrer" className="site-link">
                    <i className="material-icons open-external">open_in_new</i>
                </a>
                
                {params.apiKey == null &&
                    <div>API key must be set</div>
                }

                {params.apiKey?.length > 0 && <>
                    <div>
                        <iframe src={url + "&preview=true&updated=" + lastUpdated} width="100%" height="300px"  />
                    </div>

                    <span className="h3 mt-4">Code</span> <CopyToClipboard value={getIframeCode()} />
                    <pre>
                        {getIframeCode()}
                    </pre>
                </>}
            </div>
        </div>
    </div>;
}

export default BidComponentBuilder;